import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { partial, debounce } from 'underscore';
import { SiteConfigContext } from '@bingads-webui-campaign-react/smart-page-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  getComponentById,
  useDefaultEditorStyle,
  prependProtocolToUrl,
} from '@bingads-webui-campaign-react/smart-component-util';

import { getDataProperties } from './data-config';
import { SmartSocialLinkEditor } from './components/smart-social-link-editor';

const getStyles = () => ({
  socialLinksContainer: {
    position: 'relative',
  },
  toggle: {
    position: 'absolute',
    right: 40,
    marginTop: 5,
    zIndex: 1,
  },
});

const UnstyledSmartSocialLinksEditor = ({
  i18n,
  classes,
  componentId,
}) => {
  const {
    titleClassName,
    descriptionClassName,
    editorContentClassName,
  } = useDefaultEditorStyle();

  const { siteConfig, dispatchSiteConfig } = useContext(SiteConfigContext);
  const componentConfig = getComponentById(siteConfig, componentId) || {};
  const defautLinkInfo = {
    isDisplay: false,
    value: '',
  };

  const onValueChange = debounce((property, { value }) => {
    const linkInfo = componentConfig[property] || defautLinkInfo;
    linkInfo.value = prependProtocolToUrl(value);
    const update = {
      ...componentConfig,
      [property]: linkInfo,
    };
    dispatchSiteConfig({
      type: 'UPDATE_COMPONENT_BY_ID',
      payload: update,
    });
  }, 200);

  const onToggleChange = debounce((property, e, checked) => {
    const linkInfo = componentConfig[property] || defautLinkInfo;
    linkInfo.isDisplay = checked;
    const update = {
      ...componentConfig,
      [property]: linkInfo,
    };

    dispatchSiteConfig({
      type: 'UPDATE_COMPONENT_BY_ID',
      payload: update,
    });
  }, 80);

  const dataProperties = getDataProperties(i18n);
  const linkEditors = [];

  Object.entries(dataProperties).forEach(([, socialLinkMapping]) => {
    linkEditors.push((
      <SmartSocialLinkEditor
        i18n={i18n}
        key={socialLinkMapping.id}
        componentId={socialLinkMapping.id}
        socialLinkKey={socialLinkMapping.key}
        socialLinkLabel={socialLinkMapping.label}
        linkPlaceholder={socialLinkMapping.placeholder}
        enabled={
          !!componentConfig[socialLinkMapping.key] &&
          componentConfig[socialLinkMapping.key].isDisplay}
        value={componentConfig[socialLinkMapping.key]
          ? componentConfig[socialLinkMapping.key].value : ''}
        onValueChange={partial(onValueChange, socialLinkMapping.key)}
        onToggleChange={partial(onToggleChange, socialLinkMapping.key)}
      />
    ));
  });

  return (
    <div id="smart-page-social-links-editor-container" className={classes.socialLinksContainer}>
      <h2 className={titleClassName}>
        {i18n.getString(_TL_('Social links'))}
      </h2>
      <div className={descriptionClassName}>
        {i18n.getString(_TL_('Add links to your website, so customers can find and follow your business on social media.'))}
      </div>
      <div className={editorContentClassName}>
        {linkEditors}
      </div>
    </div>
  );
};

UnstyledSmartSocialLinksEditor.propTypes = {
  classes: PropTypes.shape({
    socialLinksContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  componentId: PropTypes.string.isRequired,
};

export const SmartSocialLinksEditor = withDefaultStyles(
  UnstyledSmartSocialLinksEditor,
  getStyles
);
